import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context';
import {
  ArrowLeft,
  Filter,
  Trash2,
  Phone,
  Mail,
  Smile,
  User,
  StickyNote,
  Meh,
  Frown,
} from 'lucide-react';
import { cn, formatDate, formatInteractionType } from '../lib/utils';
import { Interaction } from '../types';

export default function ContactHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, getContactInteractions, removeInteraction, updateContact } = useApp();
  const [filter, setFilter] = useState<'all' | 'call' | 'text' | 'in_person' | 'video'>('all');
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: 'note' | 'interaction';
  } | null>(null);

  const contact = state.contacts.find((c) => c.id === id);
  const interactions = getContactInteractions(id || '');

  if (!contact) return <div className="p-8 text-center">Contact not found</div>;

  // Handle legacy string notes to prevent crashes
  let normalizedComments: any[] = [];
  if (Array.isArray(contact?.notes)) {
    normalizedComments = contact.notes;
  } else if (typeof contact?.notes === 'string') {
    normalizedComments = [
      {
        id: 'legacy-note',
        text: contact?.notes,
        date: contact?.lastInteractionDate || new Date().toISOString().split('T')[0],
      },
    ];
  }

  const timelineItems = [
    ...interactions.map((i) => ({ type: 'interaction' as const, date: i.date, data: i })),
    ...normalizedComments.map((c) => ({ type: 'note' as const, date: c.date, data: c })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const filteredItems =
    filter === 'all'
      ? timelineItems
      : timelineItems.filter((i) => i.type === 'interaction' && (i.data as any).type === filter);

  const handleRemoveComment = (commentId: string) => {
    updateContact(contact.id, {
      notes: normalizedComments.filter((c) => c.id !== commentId),
    });
    setItemToDelete(null);
  };

  const handleDeleteInteraction = (interactionId: string) => {
    removeInteraction(interactionId);
    setItemToDelete(null);
  };

  const getFilterPillClass = (currentFilter: string) => {
    return cn(
      'px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors',
      filter === currentFilter
        ? 'bg-[#fa8585] text-white shadow-md shadow-[#fa8585]/20'
        : 'bg-white text-slate-600 border border-slate-200/60 hover:bg-slate-50',
    );
  };

  return (
    <div className="bg-[#f8f5f5] min-h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center p-4 sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 flex items-center justify-center rounded-full hover:bg-slate-200/50 text-slate-900 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1 text-center pr-10">
          <h1 className="text-lg font-bold">History</h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            {contact.name}
          </p>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Stats Card */}
        <div className="bg-[#1a1b2e] rounded-[2rem] p-6 text-white shadow-xl shadow-slate-900/10">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                Total Interactions
              </p>
              <p className="text-4xl font-bold">{interactions.length}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                Last Contact
              </p>
              <p className="text-lg font-bold text-[#fa8585]">
                {formatDate(contact.lastInteractionDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <button onClick={() => setFilter('all')} className={getFilterPillClass('all')}>
            All
          </button>
          <button onClick={() => setFilter('call')} className={getFilterPillClass('call')}>
            Call
          </button>
          <button onClick={() => setFilter('text')} className={getFilterPillClass('text')}>
            Text
          </button>
          <button
            onClick={() => setFilter('in_person')}
            className={getFilterPillClass('in_person')}
          >
            In-Person
          </button>
          <button onClick={() => setFilter('video')} className={getFilterPillClass('video')}>
            Video
          </button>
        </div>

        {/* History List */}
        {filteredItems.length > 0 ? (
          <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
            {filteredItems.map((item) => {
              if (item.type === 'note') {
                const note = item.data as any;
                return (
                  <div key={`note-${note.id}`} className="relative pl-12 pb-6">
                    <div className="absolute left-0 size-10 rounded-full bg-blue-50 text-blue-500 border-2 border-white flex items-center justify-center z-10">
                      <StickyNote size={18} />
                    </div>
                    <div className="pt-1">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Note
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-400">
                            {formatDate(note.date)}
                          </span>
                          <button
                            onClick={() => setItemToDelete({ id: note.id, type: 'note' })}
                            className="text-slate-300 hover:text-red-500 transition-all p-1"
                            title="Delete note"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.text}</p>
                    </div>
                  </div>
                );
              } else {
                const interaction = item.data as any;
                return (
                  <div key={`int-${interaction.id}`} className="relative pl-12 pb-6">
                    <div
                      className={cn(
                        'absolute left-0 size-10 rounded-full flex items-center justify-center z-10',
                        'bg-slate-50 text-slate-400 border-2 border-white',
                      )}
                    >
                      {interaction.type === 'call' && <Phone size={18} />}
                      {interaction.type === 'text' && <Mail size={18} />}
                      {interaction.type === 'in_person' && <Smile size={18} />}
                      {interaction.type === 'video' && <User size={18} />}
                    </div>
                    <div className="pt-1">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            Interaction
                          </p>
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 rounded-full bg-slate-100/50 border border-slate-200 text-[10px] font-medium text-slate-500 capitalize whitespace-nowrap">
                              {formatInteractionType(interaction.type)}
                            </span>
                            {interaction.rating && (
                              <span
                                className={cn(
                                  'px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize whitespace-nowrap flex items-center gap-1',
                                  interaction.rating === 'good'
                                    ? 'bg-green-50 text-green-600 border-green-200'
                                    : interaction.rating === 'ok'
                                      ? 'bg-yellow-50 text-yellow-600 border-yellow-200'
                                      : 'bg-red-50 text-red-600 border-red-200',
                                )}
                              >
                                {interaction.rating === 'good' && <Smile size={10} />}
                                {interaction.rating === 'ok' && <Meh size={10} />}
                                {interaction.rating === 'bad' && <Frown size={10} />}
                                {interaction.rating}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-400">
                            {formatDate(interaction.date)}
                          </span>
                          <button
                            onClick={() =>
                              setItemToDelete({ id: interaction.id, type: 'interaction' })
                            }
                            className="text-slate-300 hover:text-red-500 transition-all p-1"
                            title="Delete interaction"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      {interaction.notes && (
                        <p className="text-sm text-slate-700 mt-1">{interaction.notes}</p>
                      )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        ) : (
          <div className="py-20 flex flex-col items-center justify-center text-center px-4">
            <div className="size-16 rounded-3xl bg-slate-100 flex items-center justify-center text-slate-300 mb-4">
              <Filter size={32} />
            </div>
            <p className="text-slate-500 font-medium">No interactions found for this filter.</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <h3 className="font-bold text-lg mb-2">Delete Item?</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to remove this from your history? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setItemToDelete(null)}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (itemToDelete.type === 'note') {
                    handleRemoveComment(itemToDelete.id);
                  } else {
                    handleDeleteInteraction(itemToDelete.id);
                  }
                }}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
